import { useState, useRef, useCallback, useEffect } from 'react';
import supportsPassiveEvents from './supportsPassiveEvents.js';

/* eslint-disable @typescript-eslint/ban-ts-comment */
const cancelScroll = (event) => {
    event.preventDefault();
    event.stopPropagation();
};
function useScrollCapture(preventParent) {
    const [scrollPosition, setScrollPosition] = useState('top');
    const isBottom = useRef(false);
    const isTop = useRef(true);
    const touchStart = useRef(0);
    const scrollRef = useRef(null);
    const [initialHasScrollbar, setInitialHasScrollbar] = useState(false);
    const handleEventDelta = useCallback((event, delta) => {
        // Reference should never be `null` at this point, but flow complains otherwise
        if (scrollRef.current === null) {
            return;
        }
        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
        const target = scrollRef.current;
        const isDeltaPositive = delta > 0;
        const availableScroll = scrollHeight - clientHeight - scrollTop;
        let shouldCancelScroll = false;
        // reset bottom/top flags
        if (availableScroll > delta && isBottom.current) {
            setScrollPosition('middle');
            isBottom.current = false;
        }
        if (isDeltaPositive && isTop.current) {
            setScrollPosition('middle');
            isTop.current = false;
        }
        // bottom limit
        if (isDeltaPositive && delta > availableScroll) {
            if (!isBottom.current) {
                setScrollPosition('bottom');
            }
            target.scrollTop = scrollHeight;
            shouldCancelScroll = true;
            isBottom.current = true;
            // top limit
        }
        else if (!isDeltaPositive && -delta > scrollTop) {
            if (!isTop.current) {
                setScrollPosition('top');
            }
            target.scrollTop = 0;
            shouldCancelScroll = true;
            isTop.current = true;
        }
        // cancel scroll
        if (shouldCancelScroll) {
            cancelScroll(event);
        }
        if (preventParent) {
            event.stopPropagation();
        }
    }, [preventParent]);
    const onWheel = useCallback((event) => {
        handleEventDelta(event, event.deltaY);
        if (preventParent) {
            event.stopPropagation();
        }
    }, [handleEventDelta, preventParent]);
    const onTouchStart = useCallback((event) => {
        touchStart.current = event.changedTouches[0].clientY;
        if (preventParent) {
            event.stopPropagation();
        }
    }, [preventParent]);
    const onTouchMove = useCallback((event) => {
        const deltaY = touchStart.current - event.changedTouches[0].clientY;
        handleEventDelta(event, deltaY);
        if (preventParent) {
            event.stopPropagation();
        }
    }, [handleEventDelta, preventParent]);
    const startListening = useCallback((element) => {
        if (!element) {
            return;
        }
        const notPassive = supportsPassiveEvents ? { passive: false } : false;
        // all the if statements are to appease Flow 😢
        if (typeof element.addEventListener === 'function') {
            // @ts-ignore
            element.addEventListener('wheel', onWheel, notPassive);
        }
        if (typeof element.addEventListener === 'function') {
            // @ts-ignore
            element.addEventListener('touchstart', onTouchStart, notPassive);
        }
        if (typeof element.addEventListener === 'function') {
            // @ts-ignore
            element.addEventListener('touchmove', onTouchMove, notPassive);
        }
    }, [onTouchMove, onTouchStart, onWheel]);
    const getHasScrollbar = useCallback(() => !!scrollRef.current && scrollRef.current.scrollHeight > scrollRef.current.clientHeight, []);
    const stopListening = useCallback(element => {
        if (!element) {
            return;
        }
        if (typeof element.removeEventListener === 'function') {
            // all the if statements are to appease Flow 😢
            element.removeEventListener('wheel', onWheel, false);
        }
        if (typeof element.removeEventListener === 'function') {
            element.removeEventListener('touchstart', onTouchStart, false);
        }
        if (typeof element.removeEventListener === 'function') {
            element.removeEventListener('touchmove', onTouchMove, false);
        }
    }, [onTouchMove, onTouchStart, onWheel]);
    useEffect(() => {
        setInitialHasScrollbar(getHasScrollbar());
        const element = scrollRef.current;
        startListening(element);
        return () => {
            stopListening(element);
        };
    }, [getHasScrollbar, startListening, stopListening]);
    const hasScrollbar = getHasScrollbar();
    return {
        scrollRef,
        hasScrollbar: hasScrollbar || initialHasScrollbar,
        scrollPosition: hasScrollbar ? scrollPosition : undefined,
    };
}

export { useScrollCapture as default };
//# sourceMappingURL=useScrollCapture.js.map
